import { SUBMISSION_TYPE_ENUM } from '@data-access/shared';
import {
  DashboardWaitingOnYouResponse,
  WaitingOnYouItem,
  WaitingOnYouItemV2,
  WaitingOnYouRowsResponseV2,
} from '../types';

/** Stage/label pairs cycled across generated to-do items. */
const GENERATED_STAGES = [
  { stage: 'draft', label: 'Draft' },
  { stage: 'qc_check', label: 'Quality Check' },
  { stage: 'review', label: 'Review' },
] as const;

/** Submission types cycled across generated to-do items. */
const GENERATED_SUBMISSION_TYPES = [
  SUBMISSION_TYPE_ENUM.newStudy,
  SUBMISSION_TYPE_ENUM.newSite,
  SUBMISSION_TYPE_ENUM.newStudyandSite,
  SUBMISSION_TYPE_ENUM.singlePatientAndSite,
  SUBMISSION_TYPE_ENUM.clinicalUse,
  SUBMISSION_TYPE_ENUM.hudClinicalUseAndSite,
] as const;

/** Card messages cycled across generated to-do items. */
const GENERATED_MESSAGES = [
  'Action required: complete questionnaire before deadline.',
  'QC review pending — please upload missing documents.',
  'Reviewer requested clarification on submitted responses.',
  'Action required: upload site staff credentials.',
] as const;

/**
 * To-do entity types cycled across generated items — every known API value,
 * so mock-driven environments exercise each one. Only `question` to-dos carry
 * a questionnaire section; all other types come with null section context,
 * mirroring the API.
 */
const GENERATED_ENTITY_TYPES = [
  'question',
  'qc_mark_required',
  'document_conditionally_required',
  'document_type_required',
  'document_type_requested',
  'sme_mark_required',
  'document',
] as const;

/**
 * Builds a deterministic to-do WaitingOnYouItem for bulk mock data.
 *
 * @param index - 1-based position within the generated block; drives every
 * varying field so repeated runs produce identical output.
 * @returns A to-do item unique per index; section and survey context is
 * populated only when the cycled entity type is `question`.
 */
function buildTodoItem(index: number): WaitingOnYouItem {
  // Ids continue after the 12 handcrafted literals: 0013..0112
  const id = String(index + 12).padStart(4, '0');
  const { stage, label } = GENERATED_STAGES[index % GENERATED_STAGES.length];
  const day = String(1 + (index % 28)).padStart(2, '0');
  const sectionTotal = 5 + (index % 5);
  const entityType =
    GENERATED_ENTITY_TYPES[index % GENERATED_ENTITY_TYPES.length];
  const isQuestion = entityType === 'question';

  return {
    workspace_id: `a1b2c3d4-${id}-4e5f-8a9b-c0d1e2f3${id}`,
    workspace_name: `Generated Workspace ${id}`,
    notification_type: 'todo',
    current_stage: stage,
    current_stage_label: label,
    submission_type:
      GENERATED_SUBMISSION_TYPES[index % GENERATED_SUBMISSION_TYPES.length],
    study_name: `Generated Study ${id}`,
    sponsor_name: `Generated Sponsor ${id}`,
    sponsor_protocol_id: `GS-GEN-${id}`,
    dashboard_message: GENERATED_MESSAGES[index % GENERATED_MESSAGES.length],
    hold_reason: null,
    todo_entity_type: entityType,
    requested_date: `2026-07-${day}T09:00:00.000000`,
    section_current: isQuestion ? index % sectionTotal : null,
    section_total: isQuestion ? sectionTotal : null,
    created_dt: `2026-06-${day}T08:30:00.000000`,
    submission_id: 200000012 + index,
    survey_id: isQuestion ? `2f5eec88-b9db-4f88-8135-4969bb92${id}` : null,
    section_id: isQuestion ? `GeneratedSection-${id}` : null,
    thread_id: `51307ec6-8453-4c3b-ab74-96e9c41d${id}`,
  };
}

/** 100 generated to-do items appended after the handcrafted mock entries. */
const GENERATED_TODO_ITEMS: WaitingOnYouItem[] = Array.from(
  { length: 100 },
  (_, i) => buildTodoItem(i + 1),
);

export const WAITING_ON_YOU_MOCK: DashboardWaitingOnYouResponse = {
  count: 112,
  counts: {
    all: 112,
    todo: 110,
    on_hold: 2,
    all_submissions: 112,
    todo_submissions: 110,
    on_hold_submissions: 2,
  },
  items: [
    {
      workspace_id: 'a1b2c3d4-0001-4e5f-8a9b-c0d1e2f30001',
      workspace_name: 'Cardio Phase II Submission',
      notification_type: 'todo',
      current_stage: 'draft',
      current_stage_label: 'Draft',
      submission_type: SUBMISSION_TYPE_ENUM.newStudy,
      study_name: 'Cardio Phase II Trial',
      sponsor_name: 'HeartBio Research Inc.',
      sponsor_protocol_id: 'HB-CAR-002',
      dashboard_message:
        'Action required: complete questionnaire before deadline.',
      hold_reason: null,
      todo_entity_type: 'question',
      requested_date: '2026-07-28T09:00:00.000000',
      section_current: 3,
      section_total: 8,
      created_dt: '2026-07-20T08:30:00.000000',
      submission_id: 200000001,
      survey_id: '2f5eec88-b9db-4f88-8135-496bb9239b01',
      section_id: 'InformationforSubmitters-9ab20b',
      thread_id: '51307ec6-8453-4c3b-ab74-96e9c41d5301',
    },
    {
      workspace_id: 'a1b2c3d4-0002-4e5f-8a9b-c0d1e2f30002',
      workspace_name: 'Neuro Study Site Extension',
      notification_type: 'todo',
      current_stage: 'qc_check',
      current_stage_label: 'Quality Check',
      submission_type: SUBMISSION_TYPE_ENUM.newSite,
      study_name: 'NeuraTech Longitudinal Study',
      sponsor_name: 'NeuraTech Labs',
      sponsor_protocol_id: 'NT-NEU-017',
      dashboard_message: 'QC review pending — please upload missing documents.',
      hold_reason: null,
      todo_entity_type: 'question',
      requested_date: '2026-07-30T14:00:00.000000',
      section_current: 6,
      section_total: 6,
      created_dt: '2026-07-15T10:00:00.000000',
      submission_id: 200000002,
      survey_id: '2f5eec88-b9db-4f88-8135-496bb9239b02',
      section_id: 'StudyInformation-3cd10c',
      thread_id: '51307ec6-8453-4c3b-ab74-96e9c41d5302',
    },
    {
      workspace_id: 'a1b2c3d4-0003-4e5f-8a9b-c0d1e2f30003',
      workspace_name: 'Oncology Multi-Site Review',
      notification_type: 'on_hold',
      current_stage: 'review',
      current_stage_label: 'Review',
      submission_type: SUBMISSION_TYPE_ENUM.newStudyandSite,
      study_name: 'Oncology Adaptive Trial',
      sponsor_name: 'OncoCare Therapeutics',
      sponsor_protocol_id: 'OC-ONC-055',
      dashboard_message:
        'Submission placed on hold pending sponsor clarification.',
      hold_reason: null,
      todo_entity_type: null,
      requested_date: '2026-07-25T11:30:00.000000',
      section_current: null,
      section_total: null,
      created_dt: '2026-07-10T09:15:00.000000',
      submission_id: 200000003,
      survey_id: '2f5eec88-b9db-4f88-8135-496bb9239b03',
      section_id: 'SponsorProtocol-7ef31d',
      thread_id: '51307ec6-8453-4c3b-ab74-96e9c41d5303',
    },
    {
      workspace_id: 'a1b2c3d4-0004-4e5f-8a9b-c0d1e2f30004',
      workspace_name: 'Dermatology Device Trial',
      notification_type: 'on_hold',
      current_stage: 'qc_check',
      current_stage_label: 'Quality Check',
      submission_type: SUBMISSION_TYPE_ENUM.newStudy,
      study_name: 'DermaShield Device Study',
      sponsor_name: 'DermaShield Medical',
      sponsor_protocol_id: 'DS-DRM-021',
      dashboard_message:
        'Submission on hold — awaiting revised consent documents.',
      hold_reason: 'Missing revised informed consent form.',
      todo_entity_type: null,
      requested_date: '2026-07-27T16:45:00.000000',
      section_current: null,
      section_total: null,
      created_dt: '2026-07-12T13:20:00.000000',
      submission_id: 200000004,
      survey_id: '2f5eec88-b9db-4f88-8135-496bb9239b04',
      section_id: 'ConsentDocuments-5ab42e',
      thread_id: '51307ec6-8453-4c3b-ab74-96e9c41d5304',
    },
    {
      workspace_id: 'a1b2c3d4-0005-4e5f-8a9b-c0d1e2f30005',
      workspace_name: 'Pulmonary Phase III Submission',
      notification_type: 'todo',
      current_stage: 'draft',
      current_stage_label: 'Draft',
      submission_type: SUBMISSION_TYPE_ENUM.newStudy,
      study_name: 'AeroVance Pulmonary Trial',
      sponsor_name: 'AeroVance Pharmaceuticals',
      sponsor_protocol_id: 'AV-PUL-108',
      dashboard_message: 'Waiting for required documents.',
      hold_reason: null,
      todo_entity_type: 'document_type_requested',
      requested_date: '2026-07-29T10:15:00.000000',
      section_current: null,
      section_total: null,
      created_dt: '2026-07-21T09:00:00.000000',
      submission_id: 200000005,
      survey_id: null,
      section_id: null,
      thread_id: '51307ec6-8453-4c3b-ab74-96e9c41d5305',
    },
    {
      workspace_id: 'a1b2c3d4-0006-4e5f-8a9b-c0d1e2f30006',
      workspace_name: 'Endocrine Site Addition',
      notification_type: 'todo',
      current_stage: 'qc_check',
      current_stage_label: 'Quality Check',
      submission_type: SUBMISSION_TYPE_ENUM.newSite,
      study_name: 'GlucoBalance Registry',
      sponsor_name: 'Endocrine Health Partners',
      sponsor_protocol_id: 'EH-END-042',
      dashboard_message: 'QC feedback posted — please address reviewer notes.',
      hold_reason: null,
      todo_entity_type: 'sme_mark_required',
      requested_date: '2026-07-31T08:45:00.000000',
      section_current: null,
      section_total: null,
      created_dt: '2026-07-18T11:30:00.000000',
      submission_id: 200000006,
      survey_id: null,
      section_id: null,
      thread_id: '51307ec6-8453-4c3b-ab74-96e9c41d5306',
    },
    {
      workspace_id: 'a1b2c3d4-0007-4e5f-8a9b-c0d1e2f30007',
      workspace_name: 'Rare Disease Compassionate Use',
      notification_type: 'todo',
      current_stage: 'draft',
      current_stage_label: 'Draft',
      submission_type: SUBMISSION_TYPE_ENUM.singlePatientAndSite,
      study_name: 'OrphanCare Expanded Access',
      sponsor_name: 'OrphanCare Biosciences',
      sponsor_protocol_id: 'OB-RAR-009',
      dashboard_message:
        'Action required: complete patient eligibility section.',
      hold_reason: null,
      todo_entity_type: 'question',
      requested_date: '2026-08-01T13:00:00.000000',
      section_current: 2,
      section_total: 5,
      created_dt: '2026-07-22T14:10:00.000000',
      submission_id: 200000007,
      survey_id: '2f5eec88-b9db-4f88-8135-496bb9239b07',
      section_id: 'PatientEligibility-2fa73b',
      thread_id: '51307ec6-8453-4c3b-ab74-96e9c41d5307',
    },
    {
      workspace_id: 'a1b2c3d4-0008-4e5f-8a9b-c0d1e2f30008',
      workspace_name: 'HUD Device Clinical Use',
      notification_type: 'todo',
      current_stage: 'review',
      current_stage_label: 'Review',
      submission_type: SUBMISSION_TYPE_ENUM.clinicalUse,
      study_name: 'NeuroStim HUD Program',
      sponsor_name: 'NeuroStim Devices LLC',
      sponsor_protocol_id: 'ND-HUD-014',
      dashboard_message: 'Reviewer requested clarification on device usage.',
      hold_reason: null,
      todo_entity_type: 'qc_mark_required',
      requested_date: '2026-08-02T09:30:00.000000',
      section_current: null,
      section_total: null,
      created_dt: '2026-07-19T10:45:00.000000',
      submission_id: 200000008,
      survey_id: null,
      section_id: null,
      thread_id: '51307ec6-8453-4c3b-ab74-96e9c41d5308',
    },
    {
      workspace_id: 'a1b2c3d4-0009-4e5f-8a9b-c0d1e2f30009',
      workspace_name: 'Pediatric Vaccine Study',
      notification_type: 'todo',
      current_stage: 'draft',
      current_stage_label: 'Draft',
      submission_type: SUBMISSION_TYPE_ENUM.newStudyandSite,
      study_name: 'ImmunoKids Phase II Trial',
      sponsor_name: 'ImmunoKids Research Group',
      sponsor_protocol_id: 'IK-PED-076',
      dashboard_message: 'Action required: upload site staff credentials.',
      hold_reason: null,
      todo_entity_type: 'question',
      requested_date: '2026-08-03T15:20:00.000000',
      section_current: 3,
      section_total: 10,
      created_dt: '2026-07-23T08:00:00.000000',
      submission_id: 200000009,
      survey_id: '2f5eec88-b9db-4f88-8135-496bb9239b09',
      section_id: 'SiteStaff-6de93d',
      thread_id: '51307ec6-8453-4c3b-ab74-96e9c41d5309',
    },
    {
      workspace_id: 'a1b2c3d4-0010-4e5f-8a9b-c0d1e2f30010',
      workspace_name: 'Gastro Biologic Extension',
      notification_type: 'todo',
      current_stage: 'qc_check',
      current_stage_label: 'Quality Check',
      submission_type: SUBMISSION_TYPE_ENUM.newSite,
      study_name: 'GutHealth Biologic Study',
      sponsor_name: 'GastroNova Therapeutics',
      sponsor_protocol_id: 'GN-GAS-033',
      dashboard_message: 'QC review pending — missing site contact details.',
      hold_reason: null,
      todo_entity_type: 'document',
      requested_date: '2026-08-04T11:05:00.000000',
      section_current: null,
      section_total: null,
      created_dt: '2026-07-24T12:40:00.000000',
      submission_id: 200000010,
      survey_id: null,
      section_id: null,
      thread_id: '51307ec6-8453-4c3b-ab74-96e9c41d5310',
    },
    {
      workspace_id: 'a1b2c3d4-0011-4e5f-8a9b-c0d1e2f30011',
      workspace_name: 'Ophthalmic Laser Trial',
      notification_type: 'todo',
      current_stage: 'draft',
      current_stage_label: 'Draft',
      submission_type: SUBMISSION_TYPE_ENUM.newStudy,
      study_name: 'ClearSight Laser Study',
      sponsor_name: 'ClearSight Optics Inc.',
      sponsor_protocol_id: 'CS-OPH-058',
      dashboard_message:
        'Action required: confirm investigator brochure version.',
      hold_reason: null,
      todo_entity_type: 'question',
      requested_date: '2026-08-05T14:50:00.000000',
      section_current: 0,
      section_total: 6,
      created_dt: '2026-07-25T09:25:00.000000',
      submission_id: 200000011,
      survey_id: '2f5eec88-b9db-4f88-8135-496bb9239b11',
      section_id: 'InvestigatorBrochure-3ab15f',
      thread_id: '51307ec6-8453-4c3b-ab74-96e9c41d5311',
    },
    {
      workspace_id: 'a1b2c3d4-0012-4e5f-8a9b-c0d1e2f30012',
      workspace_name: 'HUD Cardiac Implant Program',
      notification_type: 'todo',
      current_stage: 'review',
      current_stage_label: 'Review',
      submission_type: SUBMISSION_TYPE_ENUM.hudClinicalUseAndSite,
      study_name: 'CardioMend HUD Implant Study',
      sponsor_name: 'CardioMend Medical',
      sponsor_protocol_id: 'CM-CAR-091',
      dashboard_message: 'Reviewer awaiting response on implant safety data.',
      hold_reason: null,
      todo_entity_type: 'question',
      requested_date: '2026-08-06T10:10:00.000000',
      section_current: 7,
      section_total: 8,
      created_dt: '2026-07-26T13:55:00.000000',
      submission_id: 200000012,
      survey_id: '2f5eec88-b9db-4f88-8135-496bb9239b12',
      section_id: 'SafetyData-7cd26a',
      thread_id: '51307ec6-8453-4c3b-ab74-96e9c41d5312',
    },
    ...GENERATED_TODO_ITEMS,
  ],
};

/** Review statuses cycled across generated on-hold rows; index 0 is the hold. */
const REVIEW_STATUS_CYCLE_V2 = [
  'on_hold',
  'complete',
  'in_progress',
  'complete',
] as const;

/**
 * Builds a deterministic V2 row for bulk mock data.
 *
 * @param index - 1-based position; every 4th row is on-hold, every 3rd is site-level.
 */
function buildRowV2(index: number): WaitingOnYouItemV2 {
  // Ids continue after the 4 handcrafted rows: 0005..0404
  const id = String(index + 4).padStart(4, '0');
  const { stage, label } = GENERATED_STAGES[index % GENERATED_STAGES.length];
  const day = String(1 + (index % 28)).padStart(2, '0');
  const entityType = index % 3 === 0 ? 'site' : 'study';
  const base = {
    workspace_id: `b1c2d3e4-${id}-4f5a-8b9c-d0e1f2a3${id}`,
    workspace_name: `Generated Workspace ${id}`,
    submission_id: 200000004 + index,
    current_stage: stage,
    current_stage_label: label,
    entity_type: entityType,
    submission_type:
      GENERATED_SUBMISSION_TYPES[index % GENERATED_SUBMISSION_TYPES.length],
    study_name: `Generated Study ${id}`,
    sponsor_name: `Generated Sponsor ${id}`,
    sponsor_protocol_id: `GS-GEN-${id}`,
    investigator_names:
      entityType === 'site' ? [`Investigator, Gen ${id}`] : [],
    requested_date: `2026-07-${day}T09:00:00.000000`,
    created_dt: `2026-06-${day}T08:30:00.000000`,
  } as const;

  if (index % 4 === 0) {
    const holdReason = 'Awaiting sponsor clarification';
    const reviewCount = 2 + (index % 3);
    return {
      ...base,
      notification_type: 'on_hold',
      dashboard_message: 'On hold: waiting on your response.',
      hold_reason: holdReason,
      hold_count: 1,
      review_count: reviewCount,
      reviews: Array.from({ length: reviewCount }, (_, r) => ({
        review_id: `e1f2a3b4-${id}-4c5d-8e9f-a0b1c2d3${String(r).padStart(4, '0')}`,
        review_name: 'Review',
        work_order_id: `WO-${id}${r}`,
        country: r === 0 ? 'US' : null,
        // `in_progress` = internal hold only; `complete` rows open outcome documents.
        status: REVIEW_STATUS_CYCLE_V2[r % REVIEW_STATUS_CYCLE_V2.length],
        hold_reason: r === 0 ? holdReason : null,
        status_changed_dt: `2026-07-${day}T13:20:00.000000`,
      })),
    };
  }

  const todoCount = 1 + (index % 3);
  return {
    ...base,
    notification_type: 'todo',
    dashboard_message: GENERATED_MESSAGES[index % GENERATED_MESSAGES.length],
    survey_id: `2f5eec88-b9db-4f88-8135-4969bb92${id}`,
    open_todo_count: todoCount,
    todos: Array.from({ length: todoCount }, (_, t) => {
      const isQuestion = t % 2 === 0;
      return {
        task_instance_id: `c1d2e3f4-${id}-4a5b-8c9d-e0f1a2b3${String(t).padStart(4, '0')}`,
        title: isQuestion
          ? `Confirm item ${id}-${t}`
          : `Upload document ${id}-${t}`,
        todo_kind: isQuestion ? ('question' as const) : ('document' as const),
        task_type: isQuestion ? 'question' : 'document_type_requested',
        section_id: isQuestion ? `GeneratedSection-${id}` : null,
        section_name: isQuestion ? `Generated Section ${id}` : null,
        question_id: isQuestion ? `GeneratedQuestion-${id}` : null,
        document_id: null,
        document_type_name: isQuestion ? null : 'Protocol',
        created_dt: `2026-07-${day}T10:15:00.000000`,
      };
    }),
  };
}

/** Generated rows in index order, before the `in_progress` rows are floated. */
const UNSORTED_GENERATED_ROWS_V2: WaitingOnYouItemV2[] = Array.from(
  { length: 400 },
  (_, i) => buildRowV2(i + 1),
);

/** Whether the row is on hold and carries an `in_progress` review. */
const hasInProgressReview = (row: WaitingOnYouItemV2): boolean =>
  row.notification_type === 'on_hold' &&
  row.reviews.some((review) => review.status === 'in_progress');

/** Generated on-hold rows with an `in_progress` review; listed above every other row. */
const IN_PROGRESS_ROWS_V2: WaitingOnYouItemV2[] =
  UNSORTED_GENERATED_ROWS_V2.filter(hasInProgressReview);

/** Generated rows without an `in_progress` review; listed after the handcrafted entries. */
const OTHER_GENERATED_ROWS_V2: WaitingOnYouItemV2[] =
  UNSORTED_GENERATED_ROWS_V2.filter((row) => !hasInProgressReview(row));

/** All 400 generated V2 rows, for the tab counts. */
const GENERATED_ROWS_V2: WaitingOnYouItemV2[] = [
  ...IN_PROGRESS_ROWS_V2,
  ...OTHER_GENERATED_ROWS_V2,
];

/** Rows per mock page when the request omits `page_size`. */
export const WAITING_ON_YOU_MOCK_PAGE_SIZE = 10;

/** Study-level rows among the generated V2 rows. */
const GENERATED_STUDY_ROWS_V2 = GENERATED_ROWS_V2.filter(
  (row) => row.entity_type === 'study',
).length;

/** Redesigned rows: study and site level, to-do and on-hold. */
export const WAITING_ON_YOU_ROWS_MOCK_V2: WaitingOnYouRowsResponseV2 = {
  count: 404,
  counts: {
    all: 404,
    study: 2 + GENERATED_STUDY_ROWS_V2,
    site: 2 + (400 - GENERATED_STUDY_ROWS_V2),
  },
  total_pages: Math.ceil(404 / WAITING_ON_YOU_MOCK_PAGE_SIZE),
  items: [
    ...IN_PROGRESS_ROWS_V2,
    {
      notification_type: 'todo',
      workspace_id: 'b1c2d3e4-0001-4f5a-8b9c-d0e1f2a30001',
      workspace_name: 'Cardio Phase II Submission',
      submission_id: 200000001,
      current_stage: 'qc_check',
      current_stage_label: 'Quality Check',
      entity_type: 'study',
      submission_type: SUBMISSION_TYPE_ENUM.newStudy,
      study_name: 'Cardio Phase II Trial',
      sponsor_name: 'HeartBio Research Inc.',
      sponsor_protocol_id: 'HB-CAR-002',
      investigator_names: [],
      dashboard_message: 'Action required: complete the open to-dos.',
      requested_date: '2026-07-28T09:00:00.000000',
      created_dt: '2026-07-20T08:30:00.000000',
      survey_id: '2f5eec88-b9db-4f88-8135-496bb9239b01',
      open_todo_count: 2,
      todos: [
        {
          task_instance_id: 'c1d2e3f4-0001-4a5b-8c9d-e0f1a2b30001',
          title: 'Confirm the study design',
          todo_kind: 'question',
          task_type: 'question',
          section_id: 'InformationforSubmitters-9ab20b',
          section_name: 'Information for Submitters',
          question_id: 'StudyDesign-4c1e7a',
          document_id: null,
          document_type_name: null,
          created_dt: '2026-07-27T10:15:00.000000',
        },
        {
          task_instance_id: 'c1d2e3f4-0002-4a5b-8c9d-e0f1a2b30002',
          title: 'Upload the protocol',
          todo_kind: 'document',
          task_type: 'document_type_requested',
          section_id: null,
          section_name: null,
          question_id: null,
          document_id: null,
          document_type_name: 'Protocol',
          created_dt: '2026-07-24T14:00:00.000000',
        },
      ],
    },
    {
      notification_type: 'todo',
      workspace_id: 'b1c2d3e4-0002-4f5a-8b9c-d0e1f2a30002',
      workspace_name: 'Neuro Site Extension',
      submission_id: 200000002,
      current_stage: 'qc_check',
      current_stage_label: 'Quality Check',
      entity_type: 'site',
      submission_type: SUBMISSION_TYPE_ENUM.newSite,
      study_name: 'NeuraTech Longitudinal Study',
      sponsor_name: 'NeuraTech Labs',
      sponsor_protocol_id: 'NT-LNG-014',
      investigator_names: ['Doe, Jane'],
      dashboard_message: 'Action required: upload site staff credentials.',
      requested_date: '2026-07-22T09:00:00.000000',
      created_dt: '2026-07-15T08:30:00.000000',
      survey_id: null,
      open_todo_count: 1,
      todos: [
        {
          task_instance_id: 'c1d2e3f4-0003-4a5b-8c9d-e0f1a2b30003',
          title: 'Upload the CV',
          todo_kind: 'document',
          task_type: 'document',
          section_id: null,
          section_name: null,
          question_id: null,
          document_id: 'd1e2f3a4-0001-4b5c-8d9e-f0a1b2c30001',
          document_type_name: 'Curriculum Vitae',
          created_dt: '2026-07-21T11:45:00.000000',
        },
      ],
    },
    {
      notification_type: 'on_hold',
      workspace_id: 'b1c2d3e4-0003-4f5a-8b9c-d0e1f2a30003',
      workspace_name: 'Oncology Amendment Review',
      submission_id: 200000003,
      current_stage: 'review',
      current_stage_label: 'Review',
      entity_type: 'study',
      submission_type: SUBMISSION_TYPE_ENUM.newStudy,
      study_name: 'OncoPath Phase III',
      sponsor_name: 'OncoCare Therapeutics',
      sponsor_protocol_id: 'OP-PH3-021',
      investigator_names: [],
      dashboard_message: 'On hold: waiting on your response.',
      requested_date: '2026-07-18T09:00:00.000000',
      created_dt: '2026-07-10T08:30:00.000000',
      hold_reason: 'Awaiting sponsor clarification',
      hold_count: 1,
      review_count: 3,
      reviews: [
        {
          review_id: 'e1f2a3b4-0001-4c5d-8e9f-a0b1c2d30001',
          review_name: 'Review',
          work_order_id: 'WO-100201',
          country: 'US',
          status: 'on_hold',
          hold_reason: 'Awaiting sponsor clarification',
          status_changed_dt: '2026-07-25T13:20:00.000000',
        },
        {
          review_id: 'e1f2a3b4-0002-4c5d-8e9f-a0b1c2d30002',
          review_name: 'Review',
          work_order_id: 'WO-100202',
          country: 'Canada',
          status: 'complete',
          hold_reason: null,
          status_changed_dt: '2026-07-23T16:05:00.000000',
        },
        {
          review_id: 'e1f2a3b4-0003-4c5d-8e9f-a0b1c2d30003',
          review_name: 'Review',
          work_order_id: 'WO-100203',
          country: null,
          status: 'draft',
          hold_reason: null,
          status_changed_dt: null,
        },
      ],
    },
    {
      notification_type: 'on_hold',
      workspace_id: 'b1c2d3e4-0004-4f5a-8b9c-d0e1f2a30004',
      workspace_name: 'Derma Site Addition',
      submission_id: 200000004,
      current_stage: 'review',
      current_stage_label: 'Review',
      entity_type: 'site',
      submission_type: SUBMISSION_TYPE_ENUM.newSite,
      study_name: 'DermaCare Efficacy Study',
      sponsor_name: 'DermaShield Medical',
      sponsor_protocol_id: 'DC-EFF-007',
      investigator_names: ['Smith, John', 'Lee, Ann'],
      dashboard_message: 'On hold: waiting on your response.',
      requested_date: '2026-07-12T09:00:00.000000',
      created_dt: '2026-07-05T08:30:00.000000',
      hold_reason: 'Missing investigator documents',
      hold_count: 1,
      review_count: 1,
      reviews: [
        {
          review_id: 'e1f2a3b4-0004-4c5d-8e9f-a0b1c2d30004',
          review_name: 'Review',
          work_order_id: 'WO-100204',
          country: 'US',
          status: 'on_hold',
          hold_reason: 'Missing investigator documents',
          status_changed_dt: '2026-07-20T09:40:00.000000',
        },
      ],
    },
    ...OTHER_GENERATED_ROWS_V2,
  ],
};
